/**
 * Section-override mutation pipeline (Phase 1 of pb-section-properties overhaul).
 *
 * Single source of truth for the five-emission contract that the legacy
 * `applyOverride` closure inside `_wireOverrides()` was performing. Extracted
 * into a real, importable function so drawer modules (Phases 3+) and the
 * "Reset all overrides" footer (Phase 9) can call it directly.
 *
 * Contract for `applySectionOverride(instanceId, field, value, { source })`:
 *
 *   1. store.update('pages.{p}.sections.{s}.overrides', overrides,
 *                   `Change section ${field}`)
 *   2. eventBus.emit('theme:override-changed',
 *                   { scope: 'section', instanceId, field })
 *   3. eventBus.emit('glass:spacing-changed',
 *                   { instanceId, field, value, source })
 *   4. bridge.send('query-section-positions', {})
 *   5. setTimeout(() => bridge.querySectionSpacing(), 100)
 *
 * `resetAllSectionOverrides(instanceId, { source })` clears every section and
 * element override in a single store transaction,
 * then enumerates emissions: `theme:override-changed` fires once, every
 * cleared spacing field gets its own `glass:spacing-changed` (so the canvas
 * overlay refreshes per-field), and steps 4 + 5 fire once.
 *
 * Notes:
 * - Pure module — no DOM access. Callers handle their own UI updates.
 * - Shared sections (navigation/footer) are not supported by this path —
 *   matches the legacy closure's behaviour.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { bridge } from '../../editing/bridge.js';
import { RESPONSIVE_VIEWPORTS } from '../../editing/section-utils.js';
import { ENTRANCE_OVERRIDE_FIELD } from '../../shared/entrance-presets.js';

/** All section-level override fields managed by the section-properties panel. */
export const SECTION_OVERRIDE_FIELDS = Object.freeze([
  'backgroundColor',
  'marginTop',
  'marginBottom',
  'paddingTop',
  'paddingBottom',
  'customCSS',
  'columnCount',
  'decorativeBleed',
  // Entrance animations (EA-1). NOT a spacing field: it emits no
  // `glass:spacing-changed` handle refresh, and `''` means "inherit the site
  // preset" while the literal `'none'` means "this section does not animate".
  'revealPreset'
]);

/** Spacing fields require an extra `glass:spacing-changed` emission per change. */
export const SPACING_OVERRIDE_FIELDS = Object.freeze([
  'marginTop',
  'marginBottom',
  'paddingTop',
  'paddingBottom'
]);

/** True when an override value is present (null/undefined/'' mean "not set"). */
export function isSet(value) {
  return value != null && value !== '';
}

function countElementProperties(elements) {
  let count = 0;
  for (const properties of Object.values(elements || {})) {
    if (!properties || typeof properties !== 'object') continue;
    for (const [field, value] of Object.entries(properties)) {
      if (field === '__important' || !isSet(value)) continue;
      count++;
    }
  }
  return count;
}

/** Count element-level properties in base and responsive override buckets. */
export function getElementOverrideCount(section) {
  const overrides = section?.overrides;
  if (!overrides) return 0;
  let count = countElementProperties(overrides.elements);
  for (const viewport of RESPONSIVE_VIEWPORTS) {
    count += countElementProperties(overrides.responsive?.[viewport]?.elements);
  }
  return count;
}

function isSpacingField(field) {
  return SPACING_OVERRIDE_FIELDS.indexOf(field) !== -1;
}

/**
 * The section a write targets, and the state path its overrides live at.
 *
 * SHARED SECTIONS ARE INCLUDED, and that is the whole reason this returns a
 * path rather than a page/section index pair. It used to search
 * `page.sections` only, so for a navigation or footer it returned null and
 * `applySectionOverride` returned `false` — silently. The panel does not go
 * away for a shared section: it renders, it READS the current value fine
 * (`resolveSectionByInstance` in editing/section-utils.js has always known
 * about `sharedSections`), and every write was dropped on the floor. Typing a
 * background colour into a footer did nothing, and reopening the panel showed
 * the old value because the old value was never replaced.
 *
 * The renderer needs no change to match: `collectSectionsForLiveCSS` already
 * includes the shared nav and footer, and `assembleOverridesCSS` reads each
 * section's own `overrides` wherever that section lives.
 */
function resolveOverrideTarget(instanceId) {
  const state = store.getState();
  for (const type of ['navigation', 'footer']) {
    const shared = state?.sharedSections?.[type];
    if (shared && shared.instanceId === instanceId) {
      return { section: shared, overridesPath: `sharedSections.${type}.overrides`, shared: type };
    }
  }
  const page = store.getActivePage();
  if (!page || !Array.isArray(page.sections)) return null;
  const sectionIdx = page.sections.findIndex((s) => s.instanceId === instanceId);
  if (sectionIdx === -1) return null;
  const pageIdx = state && Array.isArray(state.pages) ? state.pages.indexOf(page) : -1;
  if (pageIdx === -1) return null;
  return {
    page,
    section: page.sections[sectionIdx],
    overridesPath: `pages.${pageIdx}.sections.${sectionIdx}.overrides`,
    shared: null
  };
}

/** One field's worth of the override-shape rules, applied to a working copy. */
function applyFieldToOverrides(overrides, field, nextValue, viewport) {
  if (viewport) {
    const responsive = { ...(overrides.responsive || {}) };
    const bucket = { ...(responsive[viewport] || {}) };
    if (nextValue) bucket[field] = nextValue;
    else delete bucket[field];
    if (Object.keys(bucket).length) responsive[viewport] = bucket;
    else delete responsive[viewport];
    if (Object.keys(responsive).length) overrides.responsive = responsive;
    else delete overrides.responsive;
    return;
  }
  if (nextValue) overrides[field] = nextValue;
  else delete overrides[field];
  // "All sizes" is authoritative: clear stale named values for this field.
  const responsive = { ...(overrides.responsive || {}) };
  for (const name of RESPONSIVE_VIEWPORTS) {
    if (!responsive[name] || !Object.prototype.hasOwnProperty.call(responsive[name], field))
      continue;
    const bucket = { ...responsive[name] };
    delete bucket[field];
    if (Object.keys(bucket).length) responsive[name] = bucket;
    else delete responsive[name];
  }
  if (Object.keys(responsive).length) overrides.responsive = responsive;
  else delete overrides.responsive;
}

/**
 * Pure compute half of the override pipeline (16B2): the exact shape rules the
 * single-field path has always applied, generalized from one field to a
 * `changes` object. No store, no DOM, no emissions — the bulk writer and the
 * single-field wrapper both call this, so the two paths cannot drift.
 *
 * @param {object|null} section
 * @param {Object<string,string>} changes - field → value ('' clears)
 * @param {{ viewport?: string|null }} [opts]
 * @returns {{ overrides: object|null, changedFields: string[] }}
 */
export function computeSectionOverrides(section, changes, opts = {}) {
  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  const overrides = { ...(section?.overrides || {}) };
  const changedFields = [];
  for (const [field, rawValue] of Object.entries(changes || {})) {
    const currentValue = viewport
      ? section?.overrides?.responsive?.[viewport]?.[field] || ''
      : section?.overrides?.[field] || '';
    const nextValue = rawValue || '';
    if (currentValue === nextValue) continue;
    applyFieldToOverrides(overrides, field, nextValue, viewport);
    changedFields.push(field);
  }
  return {
    overrides: Object.keys(overrides).length > 0 ? overrides : null,
    changedFields
  };
}

/**
 * Summarize one override field across a set of sections — the mixed-value
 * contract's data half (16B2). Three states:
 *   uniform-set   → every section has the SAME non-empty value
 *   uniform-unset → no section has a value
 *   mixed         → anything else; the control shows `—` ("Mixed values"),
 *                   stays enabled, and touching it applies to all.
 *
 * @param {Array<object>} sections
 * @param {string} field
 * @param {{ viewport?: string|null }} [opts]
 * @returns {{ state: 'uniform-set'|'uniform-unset'|'mixed', value: string|null }}
 */
export function summarizeOverrideAcross(sections, field, opts = {}) {
  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  let first = '';
  let seen = false;
  for (const section of sections || []) {
    const bucket = viewport ? section?.overrides?.responsive?.[viewport] : section?.overrides;
    const value = bucket?.[field] || '';
    if (!seen) {
      first = value;
      seen = true;
      continue;
    }
    if (value !== first) return { state: 'mixed', value: null };
  }
  if (!seen || !first) return { state: 'uniform-unset', value: '' };
  return { state: 'uniform-set', value: first };
}

// ── Targeted verify events at commit (16B2) ────────────────────────────────
// A payload-less `theme:override-changed` is ignored by apply-and-verify
// (style-verify's _normalizeTarget early-returns), so a bulk scrub that only
// emitted bare events would silently skip escalation. During a transaction the
// bulk writer records every touched (instanceId, field) here and emits bare
// events per frame (CSS push + panel refresh are payload-agnostic); ONE
// targeted event per touched pair fires at commit — 6 sections × 2 fields is
// 12 emissions instead of 1,440 done per frame. Stale entries left behind by
// a net-no-op commit (16B1 emits no history-changed for those) are drained on
// the next commit and are harmless: style-verify re-reads live values and
// drops cleared/unchanged targets.
const _txnTouchedTargets = new Map(); // key → {instanceId, field, viewport}
let _commitFlushInstalled = false;

function _touchKey(instanceId, field, viewport) {
  return `${instanceId}::${field}::${viewport || ''}`;
}

function _ensureCommitFlush() {
  if (_commitFlushInstalled) return;
  _commitFlushInstalled = true;
  eventBus.on('history-changed', (evt) => {
    if (!_txnTouchedTargets.size || store.isInTransaction()) return;
    if (evt && evt.source && evt.source !== 'mutation') {
      // Undo/redo re-derive their own state; stale targets must not verify
      // against a rolled-back value.
      _txnTouchedTargets.clear();
      return;
    }
    const targets = [..._txnTouchedTargets.values()];
    _txnTouchedTargets.clear();
    for (const t of targets) {
      eventBus.emit('theme:override-changed', {
        scope: 'section',
        instanceId: t.instanceId,
        field: t.field,
        viewport: t.viewport
      });
    }
  });
}

/**
 * D9 — an entrance override needs a RENDER, not just a stylesheet update.
 *
 * `applySectionOverride` only writes state and emits override/CSS events, and
 * the shell deliberately treats override changes as CSS-only and does not
 * rerender. That is right for a colour or a padding, and wrong for an entrance:
 * the preset is stamped into section MARKUP at render time, and the first
 * non-`none` preset is also what pulls the entrance stylesheet into the
 * document. Without this the user would set a preset and see nothing until some
 * unrelated edit happened to trigger a render.
 *
 * Discrete change, so a full render is fine — this is not a scrub.
 */
function requestEntranceRerender(field) {
  if (field !== ENTRANCE_OVERRIDE_FIELD) return;
  eventBus.emit('preview:render-request', { preserveScroll: true });
}

/**
 * Mutate a single section override and run the full five-emission contract.
 *
 * @param {string} instanceId
 * @param {string} field — one of SECTION_OVERRIDE_FIELDS
 * @param {string} value — empty string / falsy clears the override
 * @param {{ source?: string }} [opts]
 * @returns {boolean} true on success, false if the section was not found
 */
export function applySectionOverride(instanceId, field, value, opts = {}) {
  const source = opts.source || 'property-panel';
  const ctx = resolveOverrideTarget(instanceId);
  if (!ctx) return false;

  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  const { overrides: nextOverrides, changedFields } = computeSectionOverrides(
    ctx.section,
    { [field]: value },
    { viewport }
  );
  if (changedFields.length === 0) return false;
  const nextValue = value || '';

  store.update(ctx.overridesPath, nextOverrides, `Change section ${field}`);

  eventBus.emit('theme:override-changed', { scope: 'section', instanceId, field, viewport });
  eventBus.emit('glass:spacing-changed', {
    instanceId,
    field,
    value: nextValue,
    source,
    viewport
  });
  requestEntranceRerender(field);
  bridge.send('query-section-positions', {});
  setTimeout(() => bridge.querySectionSpacing(), 100);
  return true;
}

/**
 * Apply overrides to N sections as ONE logical write (16B2).
 *
 * @param {string[]} instanceIds
 * @param {Object<string,string> | ((section, instanceId) => Object<string,string>)} changes
 *   Either one changes object applied to every section (typed values and
 *   presets are ABSOLUTE — all selected sections get the value), or a
 *   function returning per-section changes (a scrub is a RELATIVE delta —
 *   each section moves from its own base; the caller computes per-section
 *   values and passes this form).
 * @param {{
 *   source?: string,
 *   viewport?: string|null,
 *   lead?: string|null,
 *   transaction?: 'auto'|'ambient'|'none'
 * }} [opts]
 *   `lead` is the section that owns the visible canvas spacing handles —
 *   an explicit argument, never inferred from document order. Only the lead
 *   gets `glass:spacing-changed` echoes.
 *   transaction: 'ambient' = the caller owns an open transaction (scrub
 *   frames); 'auto' (default) = wrap this call in its own transaction when
 *   none is open; 'none' = plain one-entry write via updateMany.
 * @returns {boolean} whether anything changed
 */
export function applySectionOverridesBulk(instanceIds, changes, opts = {}) {
  const source = opts.source || 'property-panel';
  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  const lead = opts.lead || null;
  const mode = opts.transaction || 'auto';

  const mutations = [];
  const touched = [];
  let leadChanges = null;
  for (const instanceId of instanceIds || []) {
    const ctx = resolveOverrideTarget(instanceId);
    if (!ctx) continue; // property writes are non-destructive: skip, don't abort
    const perSection = typeof changes === 'function' ? changes(ctx.section, instanceId) : changes;
    const { overrides, changedFields } = computeSectionOverrides(ctx.section, perSection, {
      viewport
    });
    if (changedFields.length === 0) continue;
    mutations.push({
      path: ctx.overridesPath,
      value: overrides
    });
    for (const field of changedFields) touched.push({ instanceId, field, viewport });
    if (instanceId === lead) {
      leadChanges = changedFields.map((field) => ({
        field,
        value: (typeof changes === 'function' ? perSection : changes)?.[field] || ''
      }));
    }
  }
  if (mutations.length === 0) return false;

  const fieldNames = touched.map((t) => t.field);
  const label =
    new Set(fieldNames).size === 1
      ? `Change section ${fieldNames[0]}`
      : 'Change section properties';

  const wrap = mode === 'auto' && !store.isInTransaction();
  if (wrap) store.beginTransaction(label);
  const inTxn = store.isInTransaction();
  if (inTxn) {
    _ensureCommitFlush();
    for (const t of touched) {
      _txnTouchedTargets.set(_touchKey(t.instanceId, t.field, t.viewport), t);
    }
  }
  store.updateMany(mutations, label);
  if (inTxn) {
    // Bare event per frame: the CSS push and panel refresh listeners are
    // payload-agnostic; style-verify ignores it and gets the targeted events
    // at commit via the flush above.
    eventBus.emit('theme:override-changed');
  }
  if (wrap) store.commitTransaction();
  if (!inTxn) {
    for (const t of touched) {
      eventBus.emit('theme:override-changed', {
        scope: 'section',
        instanceId: t.instanceId,
        field: t.field,
        viewport: t.viewport
      });
    }
  }

  if (lead && leadChanges) {
    for (const change of leadChanges) {
      eventBus.emit('glass:spacing-changed', {
        instanceId: lead,
        field: change.field,
        value: change.value,
        source,
        viewport
      });
    }
  }
  // The bulk writer reaches the same field through a multi-selection, so it
  // needs the same render — once for the whole batch, not once per section.
  if (touched.some((t) => t.field === ENTRANCE_OVERRIDE_FIELD)) {
    requestEntranceRerender(ENTRANCE_OVERRIDE_FIELD);
  }
  bridge.send('query-section-positions', {});
  setTimeout(() => bridge.querySectionSpacing(), 100);
  return true;
}

/**
 * Pure clearing half of "Reset all overrides" (16B2): compute the next
 * overrides object with every section + element override removed, and the
 * list of cleared field paths. No store, no emissions.
 * @param {object|null} section
 * @returns {{ next: object|null, cleared: string[] }}
 */
export function computeClearedOverrides(section) {
  const current = section?.overrides || {};
  const cleared = [];
  const next = structuredClone(current);
  for (const field of SECTION_OVERRIDE_FIELDS) {
    if (current[field] != null && current[field] !== '') {
      cleared.push(field);
    }
    delete next[field];
    if (next.__important && typeof next.__important === 'object') delete next.__important[field];
  }
  for (const [eid, properties] of Object.entries(current.elements || {})) {
    for (const [field, value] of Object.entries(properties || {})) {
      if (field !== '__important' && isSet(value)) cleared.push(`elements.${eid}.${field}`);
    }
  }
  delete next.elements;
  if (next.__important && !Object.keys(next.__important).length) delete next.__important;
  if (current.responsive && typeof current.responsive === 'object') {
    for (const viewport of RESPONSIVE_VIEWPORTS) {
      const bucket = current.responsive[viewport];
      if (!bucket) continue;
      const nextBucket = next.responsive?.[viewport];
      for (const field of SECTION_OVERRIDE_FIELDS) {
        if (isSet(bucket[field])) cleared.push(`${viewport}.${field}`);
        if (nextBucket) {
          delete nextBucket[field];
          if (nextBucket.__important && typeof nextBucket.__important === 'object') {
            delete nextBucket.__important[field];
          }
        }
      }
      for (const [eid, properties] of Object.entries(bucket.elements || {})) {
        for (const [field, value] of Object.entries(properties || {})) {
          if (field !== '__important' && isSet(value)) {
            cleared.push(`${viewport}.elements.${eid}.${field}`);
          }
        }
      }
      if (nextBucket) {
        delete nextBucket.elements;
        if (nextBucket.__important && !Object.keys(nextBucket.__important).length) {
          delete nextBucket.__important;
        }
        if (!Object.keys(nextBucket).length) delete next.responsive[viewport];
      }
    }
    if (next.responsive && !Object.keys(next.responsive).length) delete next.responsive;
  }

  return { next: Object.keys(next).length ? next : null, cleared };
}

/** Emit the canvas-refresh tail every cleared spacing band needs. */
function _emitClearedSpacing(instanceId, cleared, source) {
  for (const path of cleared) {
    // Element-level clears refresh via state/history listeners; only
    // section-level spacing fields notify the canvas handles directly.
    if (path.includes('elements.')) continue;
    const field = path.split('.').at(-1);
    if (isSpacingField(field)) {
      eventBus.emit('glass:spacing-changed', {
        instanceId,
        field,
        value: '',
        source
      });
    }
  }
}

/**
 * Clear every non-empty section or element override in a single store transaction
 * and enumerate emissions correctly so the canvas overlay (selection outline,
 * spacing badges) refreshes for each cleared spacing band.
 *
 * @param {string} instanceId
 * @param {{ source?: string }} [opts]
 * @returns {{ cleared: string[] } | null} list of cleared field names, or null
 *   if the section was not found.
 */
export function resetAllSectionOverrides(instanceId, opts = {}) {
  const source = opts.source || 'reset-all';
  const ctx = resolveOverrideTarget(instanceId);
  if (!ctx) return null;

  const { next, cleared } = computeClearedOverrides(ctx.section);
  if (cleared.length === 0) return { cleared };

  store.update(ctx.overridesPath, next, 'Reset section overrides');

  eventBus.emit('theme:override-changed');
  _emitClearedSpacing(instanceId, cleared, source);
  // Reset-all can clear an entrance override too, and clearing it changes
  // MARKUP exactly as setting it does.
  if (cleared.includes(ENTRANCE_OVERRIDE_FIELD)) {
    requestEntranceRerender(ENTRANCE_OVERRIDE_FIELD);
  }
  bridge.send('query-section-positions', {});
  setTimeout(() => bridge.querySectionSpacing(), 100);
  return { cleared };
}

/**
 * Clear every override on N sections as ONE write (16B2). One updateMany
 * (one history entry via 16B1), one bare `theme:override-changed`, canvas
 * spacing echoes for the LEAD's cleared bands only.
 *
 * @param {string[]} instanceIds
 * @param {{ source?: string, lead?: string|null }} [opts]
 * @returns {{ clearedById: Map<string, string[]>, total: number }}
 */
export function resetAllSectionOverridesBulk(instanceIds, opts = {}) {
  const source = opts.source || 'reset-all';
  const lead = opts.lead || null;
  const mutations = [];
  const clearedById = new Map();
  let total = 0;
  for (const instanceId of instanceIds || []) {
    const ctx = resolveOverrideTarget(instanceId);
    if (!ctx) continue;
    const { next, cleared } = computeClearedOverrides(ctx.section);
    if (cleared.length === 0) continue;
    mutations.push({
      path: ctx.overridesPath,
      value: next
    });
    clearedById.set(instanceId, cleared);
    total += cleared.length;
  }
  if (mutations.length === 0) return { clearedById, total: 0 };

  store.updateMany(mutations, 'Reset section overrides');

  eventBus.emit('theme:override-changed');
  if (lead && clearedById.has(lead)) {
    _emitClearedSpacing(lead, clearedById.get(lead), source);
  }
  for (const cleared of clearedById.values()) {
    if (!cleared.includes(ENTRANCE_OVERRIDE_FIELD)) continue;
    requestEntranceRerender(ENTRANCE_OVERRIDE_FIELD);
    break;
  }
  bridge.send('query-section-positions', {});
  setTimeout(() => bridge.querySectionSpacing(), 100);
  return { clearedById, total };
}

/**
 * Cheap helper used by the breadcrumb suffix and the "Reset all overrides"
 * pill (Phase 9). Decorative SVG insertions and form-builder edits are content
 * mutations and remain excluded.
 */
export function getSectionOverrideCount(section) {
  if (!section || !section.overrides) return 0;
  let n = 0;
  for (const field of SECTION_OVERRIDE_FIELDS) {
    const v = section.overrides[field];
    if (v != null && v !== '') n++;
  }
  for (const viewport of RESPONSIVE_VIEWPORTS) {
    const bucket = section.overrides.responsive?.[viewport] || {};
    for (const field of SECTION_OVERRIDE_FIELDS) {
      if (bucket[field] != null && bucket[field] !== '') n++;
    }
  }
  n += getElementOverrideCount(section);
  return n;
}
